import { getClient } from '@gonfalon/openapi/client';
import { createDataset } from '@gonfalon/openapi/operations/createDataset';
import { deleteDataset } from '@gonfalon/openapi/operations/deleteDataset';
import type { DownloadDatasetFileFromPgRoutePathParams } from '@gonfalon/openapi/operations/downloadDatasetFileFromPgRoute';
import { getDataset } from '@gonfalon/openapi/operations/getDataset';
import { getTracePromotionJob } from '@gonfalon/openapi/operations/getTracePromotionJob';
import { listDatasetErrors } from '@gonfalon/openapi/operations/listDatasetErrors';
import { listDatasetRows } from '@gonfalon/openapi/operations/listDatasetRows';
import { listDatasetRowVersions } from '@gonfalon/openapi/operations/listDatasetRowVersions';
import { listDatasets } from '@gonfalon/openapi/operations/listDatasets';
import { patchDataset } from '@gonfalon/openapi/operations/patchDataset';
import { postDatasetRowChanges } from '@gonfalon/openapi/operations/postDatasetRowChanges';
import { promoteTraceToDataset } from '@gonfalon/openapi/operations/promoteTraceToDataset';
import { requestNewDatasetUploadUrl } from '@gonfalon/openapi/operations/requestNewDatasetUploadUrl';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { useMutation } from '@tanstack/react-query';

import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const listDatasetsQuery = createQueryOptions(listDatasets);
export const getDatasetQuery = createQueryOptions(getDataset);
export const getDatasetErrorsQuery = createQueryOptions(listDatasetErrors);
export const listDatasetRowsQuery = createQueryOptions(listDatasetRows);
export const listDatasetRowVersionsQuery = createQueryOptions(listDatasetRowVersions);
export const getTracePromotionJobQuery = createQueryOptions(getTracePromotionJob);

export const useCreateDataset = createMutationHook(createDataset, {
  onSuccess: () => {
    const queryClient = getQueryClient();
    // Best-effort: a failed list refresh must not turn a successful create into a rejection.
    void queryClient.invalidateQueries({
      queryKey: listDatasetsQuery.partialQueryKey(),
    });
  },
});

export const useDatasetUploadUrl = createMutationHook(requestNewDatasetUploadUrl);

// Promotion is asynchronous (202 + job), so there is no cache invalidation here: the row only
// exists once the job completes, which callers observe by polling getTracePromotionJobQuery.
export const usePromoteTraceToDataset = createMutationHook(promoteTraceToDataset);

export type DatasetFileDownload = { file: Blob; filename?: string };

async function downloadDatasetFile({
  projKey,
  datasetId,
}: DownloadDatasetFileFromPgRoutePathParams): Promise<DatasetFileDownload> {
  // The generated operation parses the body as JSON, so the client is called directly to keep
  // the CSV or JSONL body as a blob.
  // oxlint-disable-next-line new-cap -- GET is an openapi-fetch method, not a constructor
  const request = getClient().GET('/internal/projects/{projKey}/datasets/{datasetId}/download', {
    params: { path: { projKey, datasetId } },
    parseAs: 'blob',
  });

  const file = await reactQueryResponseAdapter(request);
  const { response } = await request;

  return {
    file,
    filename: filenameFromContentDisposition(response.headers.get('Content-Disposition')),
  };
}

function filenameFromContentDisposition(header: string | null) {
  if (!header) {
    return undefined;
  }

  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded !== undefined) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // Fall back to the ASCII parameter below.
    }
  }

  return header
    .match(/filename=(?:"([^"]+)"|([^";]+))/i)
    ?.slice(1)
    .find(Boolean)
    ?.trim();
}

export function useDownloadDatasetFile() {
  return useMutation({
    mutationFn: downloadDatasetFile,
  });
}

export const useDeleteDataset = createMutationHook(deleteDataset);

export const usePatchDataset = createMutationHook(patchDataset, {
  onSuccess: async (_data, { projKey, datasetId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({ queryKey: listDatasetsQuery.partialQueryKey() });
    await queryClient.invalidateQueries({
      queryKey: getDatasetQuery.partialQueryKey({ projKey, datasetId }),
    });
  },
});

export const usePostDatasetRowChanges = createMutationHook(postDatasetRowChanges, {
  onSuccess: async (_data, { projKey, datasetId }) => {
    const queryClient = getQueryClient();
    await queryClient.invalidateQueries({
      queryKey: listDatasetRowsQuery.partialQueryKey({ projKey, datasetId }),
    });
    await queryClient.invalidateQueries({
      queryKey: getDatasetQuery.partialQueryKey({ projKey, datasetId }),
    });
    // The changes API commits a new dataset version, which the viewer reads to label
    // the rows it is showing.
    await queryClient.invalidateQueries({
      queryKey: listDatasetRowVersionsQuery.partialQueryKey({ projKey, datasetId }),
    });
  },
});
